<script setup lang="ts">
import { ref } from 'vue';

import UiIcon from '@@/lib/ui-toolkit/components/UiIcon.vue';
import { IconMGithub } from '@@/lib/ui-toolkit/icons';

import UiMessage from '@@/lib/ui-toolkit/components/UiMessage.vue';
import LogoOE from '@/assets/images/logo-oe.svg?component';
import LogoOM from '@/assets/images/logo-om.svg?component';
import LogoTSI from '@/assets/images/logo-tsi.svg?component';
import ContentContainer from '@/components/layout/ContentContainer.vue';

const email = ref('');
const subscribed = ref<boolean>();

const subscribeEmail = async () => {
  try {
    await $fetch('/api/contact', {
      method: 'POST',
      body: { email: email.value },
    });
    email.value = '';
    subscribed.value = true;
  } catch {
    subscribed.value = false;
  }
};
</script>

<template>
  <footer class="footer">
    <ContentContainer class="subscribe">
      <div class="logos logos-primary">
        <LogoOM class="logo-om" />
        <NuxtLink to="https://superpowerinstitute.com.au/" target="_blank" class="logo-link">
          <LogoTSI class="logo-tsi" />
        </NuxtLink>
      </div>
      <div class="subscribe-copy">
        <strong>Stay Connected</strong> — Sign up to be notified of platform
        updates, events and the latest analysis.
      </div>
      <form class="subscribe-form" @submit.prevent="subscribeEmail">
        <input
          v-model="email"
          type="email"
          name="subscriber"
          placeholder="Email address"
          class="body-small subscribe-form-input"
        >
        <BaseButton
          class="subscribe-submit"
          type="submit"
        >
          Subscribe
        </BaseButton>
      </form>
      <template v-if="subscribed === true">
        <UiMessage type="info" class="subscribe-message">
          Thanks for your interest in Open Methane - we'll be in touch periodically with news and updates.
        </UiMessage>
      </template>
      <template v-if="subscribed === false">
        <UiMessage type="danger" class="subscribe-message">
          Uh oh, something went wrong. Please try again or contact
          <a href="mailto:inquiries@openmethane.org">inquiries@openmethane.org</a>
          if you haven't received a confirmation email.
        </UiMessage>
      </template>
    </ContentContainer>
    <ContentContainer class="footer-content body-small">
      <div class="footer-content-first">
        <div class="footer-contact">
          For any inquiries, suggestions, or feedback, contact the Open Methane team at
          <a href="mailto:inquiries@openmethane.org">inquiries@openmethane.org</a>.
        </div>
        <div class="footer-acknowledgement">
          <h5>Acknowledgement of Country</h5>
          <p>
            We pay our respects to the Traditional Custodians of the lands on
            which we live and work, as well as the Traditional Custodians of
            the lands and waters we may visit during our work. We acknowledge
            their Elders, past and present.
          </p>
          <div class="flags">
            <img
              src="~/assets/images/flags/aboriginal-flag.svg?url"
              alt="Aboriginal flag"
            >
            <img
              src="~/assets/images/flags/lgbtqi-flag.svg?url"
              alt="LGBTQI flag"
            >
            <img
              src="~/assets/images/flags/tsi-flag.svg?url"
              alt="Torres Strait Islands flag"
            >
          </div>
        </div>
      </div>
      <div class="footer-content-second">
        <div class="footer-content-links">
          <NuxtLink to="/methodology" class="pageLink">
            Methodology
          </NuxtLink>
          <NuxtLink to="/why-methane-matters" class="pageLink">
            Methane Matters
          </NuxtLink>
          <NuxtLink to="/about" class="pageLink">
            About Us
          </NuxtLink>
        </div>
        <div>
          <h5>Transparency & Accountability</h5>
          <p>
            We are committed to upholding the
            <a href="https://www.superpowerinstitute.com.au/open-data-tools" target="_blank">
              Open, Accessible, Auditable Data Framework
            </a>, ensuring that our data and methodology are transparent,
            verifiable, and easily accessible to all.
          </p>
          <p>
            Our data sources and open-source code are available for review and
            collaboration, aimed at fostering a spirit of openness and shared
            learning in industry and the methane research community.
          </p>
        </div>
        <div class="footer-external-links">
          <div class="logos">
            <a href="https://openelectricity.org.au" target="_blank"><LogoOE class="logo-oe" /></a>
            <a href="https://www.superpowerinstitute.com.au" target="_blank"><LogoTSI class="logo-tsi" /></a>
          </div>
          <div class="logos">
            <a href="https://github.com/openmethane" target="_blank">
              <UiIcon size="m">
                <IconMGithub />
              </UiIcon>
            </a>
          </div>
        </div>
      </div>
    </ContentContainer>
  </footer>
</template>

<style lang="scss" scoped>
@use "@/assets/styles/shared/screen" as screen;

.footer {
  width: 100%;
  margin-top: var(--spacing-xxl);
  border-top: 0.1rem solid rgba(0, 0, 0, 0.5);

  @include screen.respond(tablet) {
    margin-top: 0;
  }
}
.subscribe {
  background-color: var(--colour-off-white);
  padding: 4rem 0;

  :deep(.content-container) {
    @include screen.gridContainer();
  }
}
.logos {
  display: flex;
  align-items: center;
  gap: var(--spacing-ml);
}
.logos-primary {
  @include screen.grid(3, 8, 4);

  @include screen.respond(tablet) {
    justify-content: center;
    padding-top: var(--spacing-l);
    padding-bottom: var(--spacing-l);
    border-bottom: 0.1rem solid var(--colour-warm-grey);
    margin-bottom: var(--spacing-m);
  }
}
.logo-oe {
  height: 2rem;
}
.logo-om {
  height: 2rem;
}
.logo-tsi {
  height: 3rem;
}
.logo-link {
  svg {
    display: block;
  }
}
.subscribe-copy {
  @include screen.grid(4, 8, 4);
  @include screen.respond(tablet) {
    text-align: center;
  }
}
.subscribe-form {
  display: flex;

  @include screen.grid(5, 8, 4);
}
.subscribe-form-input {
  display: block;
  width: 100%;
  border-radius: 50vh;
  padding: 0 7rem 0 2rem;
  height: 5rem;
  background: #fff;
  border: 0.1rem solid rgba(#000, 0.5);

  &:focus {
    border: 0.1rem solid rgba(#000, 1);
  }
}
.subscribe-submit {
  margin-left: -5rem;
  height: 5rem;
  padding-left: 3.2rem;
  padding-right: 3.2rem;
}
.subscribe-message {
  grid-column: 8 / span 6;

  @include screen.respond(tablet) {
    grid-column: 1 / span 6;
  }
}

.footer-content {
  padding: var(--spacing-xl) 0;
  color: var(--colour-dark-grey);

  :deep(.content-container) {
    @include screen.gridContainer();

    @include screen.respond(tablet) {
      grid-row-gap: 0;
    }
  }
}
.footer-content-first {
  @include screen.grid(7, 8, 4);
}
.footer-content-second {
  container-type: inline-size;

  @include screen.grid(5, 8, 4);

  @include screen.respond(tablet) {
    margin-top: var(--spacing-l);
    padding-top: var(--spacing-l);
    border-top: 0.1rem solid var(--colour-warm-grey);
  }
}
.footer-contact {
  margin-bottom: var(--spacing-ml);
}
.flags {
  display: flex;
  gap: 1rem;

  & img {
    height: 2rem;
    width: auto;
  }
}
.footer-content-links {
  display: flex;
  gap: var(--spacing-s) var(--spacing-m);
  margin-bottom: var(--spacing-xl);
  font-size: 1.6rem;

  a {
    text-wrap: nowrap;
  }

  @container (width < 450px) {
    flex-direction: column;
    margin-bottom: var(--spacing-m);
  }
}
.footer-external-links {
  display: flex;
  justify-content: space-between;
  margin-top: var(--spacing-ml);
}
</style>
